import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { Button } from 'shared/components/button';
import { Text } from 'shared/components/text';
import { Image } from 'shared/components/image';
import { ApproximateLocation } from 'shared/library/approximate-location';
import { useApproximateLocation } from 'shared/library/use-approximate-location.web';
import { useEvidensiaClinics } from 'shared/library/use-evidensia-clinics.web';
import {
  EvidensiaClinic,
  getNearbyEvidensiaClinics,
  hasEvidensiaClinicLocation,
} from 'shared/library/evidensia-clinics';
import { EvidensiaLogo } from 'shared/components/evidensia-logo.web';
import { EvidensiaClinicOpeningStatus } from 'shared/components/evidensia-clinic-contact.web';
import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { preloadModal } from 'shared/components/lazy-modal.web';
import { analytics } from 'shared/library/analytics';
import { useInViewOnce } from 'shared/library/use-in-view';
import { useCurrentRoute } from 'shared/library/routes/route';
import { staticImage } from 'shared/library/static-images';
import { withEvidensiaUtm } from 'shared/library/evidensia-links';
import {
  EvidensiaClinicAction,
  getEvidensiaWidgetAnalyticsParams,
} from 'shared/components/evidensia-widget-analytics';

const loadClinicsBrowser = () =>
  import('./evidensia-clinics-browser.web').then((module) => module.EvidensiaClinicsBrowser);
const ClinicsBrowser = dynamic(loadClinicsBrowser, { ssr: false });
type ClinicOption = EvidensiaClinic & { distanceInKm?: number };

export type EvidensiaBookingWidgetProps = {
  location?: ApproximateLocation | null;
  focus?: 'seller' | 'buyer';
  isBreeder?: boolean;
  animal?: 'dog' | 'cat';
  breedId?: number | null;
  variant?: 'standard' | 'compact';
  className?: string;
};

export function EvidensiaBookingWidget({
  location = null,
  focus = 'seller',
  isBreeder = false,
  animal = 'dog',
  breedId,
  variant = 'standard',
  className,
}: EvidensiaBookingWidgetProps) {
  const { t } = useTranslation('components');
  const copy = (key: string) => t(`evidensia_booking_widget.${key}`);
  const compact = variant === 'compact';
  const privateSeller = focus === 'seller' && !isBreeder;
  const offerUrl = withEvidensiaUtm(
    privateSeller
      ? 'https://app.evidensia.se/booking-login?clinicId=0'
      : focus === 'seller'
        ? 'https://evidensia.se/uppfodare/'
        : 'https://evidensia.se/vara-tjanster/halsokontroll-valp-kattunge/',
  );
  const currentRoute = useCurrentRoute();
  const widgetAnalyticsParams = useMemo(
    () =>
      getEvidensiaWidgetAnalyticsParams({
        variant,
        focus,
        animal,
        breedId,
        routeName: currentRoute.name,
      }),
    [variant, focus, animal, breedId, currentRoute.name],
  );
  const widgetRef = useInViewOnce(
    useCallback(() => {
      analytics.event('widget_impression', widgetAnalyticsParams);
      void Promise.all([preloadModal(), loadClinicsBrowser()]).catch(() => {
        // A click can retry a failed preload through the normal modal loaders.
      });
    }, [widgetAnalyticsParams]),
  );
  const track = useCallback(
    (event: string, clinicId?: number | null) => {
      analytics.event(event, {
        ...widgetAnalyticsParams,
        ...(clinicId ? { clinic_id: clinicId } : {}),
      });
    },
    [widgetAnalyticsParams],
  );
  const trackClinicAction = useCallback(
    (action: EvidensiaClinicAction, clinic: EvidensiaClinic) => {
      track(`widget_clinic_${action}`, clinic.id);
    },
    [track],
  );
  const { location: coordinate } = useApproximateLocation(location);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const selectClinic = useCallback(
    (id: number) => {
      setSelectedId(id);
      track('widget_select_clinic', id);
    },
    [track],
  );
  const treatment =
    focus === 'seller' ? 'id-marking' : animal === 'dog' ? 'vaccination-dog' : 'vaccination-cat';
  const { clinics, loading, failed } = useEvidensiaClinics(treatment);
  const latitude = coordinate?.latitude;
  const longitude = coordinate?.longitude;
  const options: ClinicOption[] = useMemo(
    () =>
      latitude !== undefined && longitude !== undefined
        ? getNearbyEvidensiaClinics(clinics, { latitude, longitude }, clinics.length)
        : clinics,
    [clinics, latitude, longitude],
  );
  const selected =
    options.find((clinic) => clinic.id === selectedId) ??
    options.find(hasEvidensiaClinicLocation) ??
    options[0];
  const clinicId = selected?.id;
  const summaryImageUrl =
    focus === 'buyer'
      ? staticImage(animal === 'cat' ? 'kitten-litter' : 'puppy-by-table')
      : selected?.imageUrl;
  const offerCondition = copy(
    focus === 'buyer'
      ? `buyer_condition_${animal}`
      : compact
        ? 'seller_condition_compact'
        : 'seller_condition',
  );

  const offerMessage = (
    <View className={compact ? 'gap-1' : 'gap-2'}>
      <Text
        className={clsx('text-brand-brown-dark/80 text-sm', compact ? 'leading-5' : 'leading-6')}
      >
        {privateSeller ? (
          copy('private_seller_description')
        ) : focus === 'buyer' ? (
          <>
            {copy(compact ? 'buyer_description_compact' : 'buyer_description')}{' '}
            <span className={'whitespace-nowrap'}>
              {copy('offer_for')}{' '}
              <span className={'font-semibold'}>{copy('buyer_offer_price')}</span>
            </span>
          </>
        ) : (
          <>
            <span className={'whitespace-nowrap font-semibold'}>
              {copy('seller_offer_discount')}
            </span>{' '}
            {copy(compact ? 'seller_description_compact' : 'seller_description')}
          </>
        )}
        {!privateSeller && <> {offerCondition}</>}
      </Text>
    </View>
  );

  return (
    <View
      ref={widgetRef}
      className={clsx(
        'border-brand-evidensia overflow-hidden rounded border-2 bg-white [container-type:inline-size]',
        className,
      )}
      testID={'evidensia-booking-widget'}
    >
      <View
        className={compact ? 'p-4' : 'p-4 [@container(min-width:40rem)]:p-5'}
        testID={'evidensia-summary'}
      >
        {compact ? (
          <View className={'min-w-0 flex-row items-center gap-3'}>
            <View className={'min-w-0 flex-1'}>{offerMessage}</View>
            <EvidensiaLogo align={'right'} size={'small'} />
          </View>
        ) : (
          <View
            className={
              'grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4 [@container(min-width:32rem)]:gap-6'
            }
          >
            <View className={'min-w-0 gap-2'}>
              <Text
                className={'text-brand-brown-dark min-w-0 text-balance text-xl font-bold leading-7'}
              >
                {copy(
                  privateSeller
                    ? 'private_seller_title'
                    : focus === 'seller'
                      ? 'seller_title'
                      : `buyer_title_${animal}`,
                )}
              </Text>
              {offerMessage}
            </View>
            <EvidensiaLogo align={'right'} />
          </View>
        )}
      </View>
      <View
        className={
          'bg-brand-evidensia gap-4 p-4 [@container(min-width:40rem)]:flex-row [@container(min-width:40rem)]:items-center'
        }
        testID={'evidensia-clinic-section'}
      >
        <View
          className={'min-w-0 flex-1 flex-row items-center gap-3'}
          testID={'evidensia-clinic-summary'}
        >
          {summaryImageUrl && (
            <Image
              src={summaryImageUrl}
              alt={''}
              width={96}
              aspectRatio={1}
              containerClassName={clsx(
                'relative shrink-0 overflow-hidden rounded',
                compact ? 'size-10' : 'size-16',
              )}
              imageClassName={'object-cover'}
            />
          )}
          <View className={'min-w-0 flex-1 gap-1.5'}>
            <View className={'flex-row items-baseline gap-2'}>
              <Text
                className={clsx(
                  'min-w-0 shrink font-semibold leading-snug text-white',
                  compact ? 'text-sm' : 'text-base',
                )}
                numberOfLines={1}
              >
                {selected
                  ? selected.name.replace(/ Evidensia$/, '').replace(/^Evidensia /, '')
                  : copy(loading ? 'loading' : failed ? 'unavailable' : 'no_results')}
              </Text>
              {selected?.distanceInKm !== undefined && (
                <Text
                  className={'shrink-0 whitespace-nowrap text-xs text-white/80'}
                  testID={'evidensia-clinic-distance'}
                >
                  {selected.distanceInKm.toLocaleString('sv-SE', {
                    maximumFractionDigits: selected.distanceInKm < 10 ? 1 : 0,
                  })}{' '}
                  km
                </Text>
              )}
            </View>
            {selected && <EvidensiaClinicOpeningStatus clinic={selected} light showHours />}
          </View>
        </View>
        <View
          className={'flex-row flex-wrap gap-2 [@container(min-width:40rem)]:w-80'}
          testID={'evidensia-actions'}
        >
          <Button
            text={copy(privateSeller ? 'book_time' : 'book')}
            icon={falAngleRight}
            color={'white'}
            dense
            className={'min-h-11 min-w-max flex-1 !border-transparent [&_svg]:shrink-0'}
            textClassName={'whitespace-nowrap !text-sm'}
            href={offerUrl}
            openInNewTab
            onPress={() => track('widget_book_visit', clinicId)}
          />
          <FullscreenModal
            headerTitle={copy('clinics_modal_title')}
            centered
            scrollable={false}
            className={'md:h-[min(44rem,96dvh)] md:max-w-4xl'}
            contentTestID={'evidensia-clinics-modal'}
            onClose={() => track('widget_close_clinics', clinicId)}
            trigger={(modal) => (
              <Button
                text={copy('show_map')}
                icon={falAngleRight}
                dense
                color={'white'}
                className={'min-h-11 min-w-max flex-1 !border-transparent [&_svg]:shrink-0'}
                textClassName={'whitespace-nowrap !text-sm'}
                onPress={() => {
                  track('widget_show_clinics', clinicId);
                  modal.open();
                }}
              />
            )}
          >
            {options.length > 0 ? (
              <ClinicsBrowser
                clinics={clinics}
                location={coordinate}
                selectedId={selected?.id ?? null}
                onSelect={selectClinic}
                onClinicAction={trackClinicAction}
              />
            ) : (
              <View className={'flex-1 items-center justify-center p-4'}>
                <Text className={'text-brand-brown-dark/70 text-sm'}>
                  {copy(loading ? 'loading' : failed ? 'unavailable' : 'no_results')}
                </Text>
              </View>
            )}
          </FullscreenModal>
        </View>
      </View>
    </View>
  );
}
